import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Organisation, organisationsApi, OrganisationGuideline, Guideline } from '../../../api';

import AddGuidelineDialog from './AddGuidelineDialog';
import OrganisationUsersTab from './OrganisationUsersTab';
import EditOrganisationDialog from './EditOrganisationDialog';
import OrganisationGuidelinesTable from './OrganisationGuidelinesTable';

const OrganisationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [guidelines, setGuidelines] = useState<(OrganisationGuideline & { guideline?: Guideline })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addGuidelineDialogOpen, setAddGuidelineDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const fetchOrganisation = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await organisationsApi.get(id);
      setOrganisation(response.data);
    } catch (err) {
      console.error('Failed to fetch organisation:', err);
      setError('Failed to fetch organisation details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchGuidelines = useCallback(async () => {
    if (!id) return;
    try {
      const response = await organisationsApi.listGuidelines(id);
      setGuidelines(response.data.items);
    } catch (err) {
      console.error('Failed to fetch guidelines:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchOrganisation();
    fetchGuidelines();
  }, [fetchOrganisation, fetchGuidelines]);

  const handleRemoveGuideline = async (guidelineId: string) => {
    if (!id || !window.confirm('Remove this guideline from the organisation?')) return;

    try {
      await organisationsApi.removeGuideline(id, guidelineId);
      fetchGuidelines();
    } catch (err) {
      console.error('Failed to remove guideline:', err);
      alert('Failed to remove guideline');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !organisation) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/organisations')}>
          Back to Organisations
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Organisation not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/organisations')}
          sx={{ mb: 2 }}
        >
          Back to Organisations
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {organisation.name}
            </Typography>
            {organisation.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {organisation.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={organisation.is_active ? 'Active' : 'Inactive'}
              color={organisation.is_active ? 'success' : 'default'}
            />
            <Chip label={organisation.subscription_tier || 'basic'} color="primary" />
            <Tooltip title="Edit Organisation">
              <IconButton onClick={() => setEditDialogOpen(true)} color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)}>
          <Tab label="Details" />
          <Tab label={`Guidelines (${guidelines.length})`} />
          <Tab label={`Users (${organisation.user_count ?? 0})`} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Organisation ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {organisation.organisation_id}
              </Typography>
            </Grid>

            {organisation.domain && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Domain
                </Typography>
                <Typography variant="body2">{organisation.domain}</Typography>
              </Grid>
            )}

            {organisation.contact_email && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Contact Email
                </Typography>
                <Typography variant="body2">{organisation.contact_email}</Typography>
              </Grid>
            )}

            {organisation.contact_phone && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Contact Phone
                </Typography>
                <Typography variant="body2">{organisation.contact_phone}</Typography>
              </Grid>
            )}

            {(organisation.address_line1 || organisation.city || organisation.country) && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Address
                </Typography>
                {organisation.address_line1 && <Typography variant="body2">{organisation.address_line1}</Typography>}
                {organisation.address_line2 && <Typography variant="body2">{organisation.address_line2}</Typography>}
                {(organisation.city || organisation.state || organisation.postal_code) && (
                  <Typography variant="body2">
                    {[organisation.city, organisation.state, organisation.postal_code].filter(Boolean).join(', ')}
                  </Typography>
                )}
                {organisation.country && <Typography variant="body2">{organisation.country}</Typography>}
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Max Users
              </Typography>
              <Typography variant="body2">{organisation.max_users || 0}</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Max Guidelines
              </Typography>
              <Typography variant="body2">{organisation.max_guidelines || 0}</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Active Users
              </Typography>
              <Typography variant="body2">{organisation.user_count ?? 0}</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Assigned Guidelines
              </Typography>
              <Typography variant="body2">{organisation.guideline_count ?? 0}</Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Created At
              </Typography>
              <Typography variant="body2">
                {new Date(organisation.created_at).toLocaleString()}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Updated At
              </Typography>
              <Typography variant="body2">
                {new Date(organisation.updated_at).toLocaleString()}
              </Typography>
            </Grid>

            {organisation.created_by && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Created By
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {organisation.created_by}
                </Typography>
              </Grid>
            )}

            {organisation.updated_by && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Updated By
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {organisation.updated_by}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {activeTab === 1 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Assigned Guidelines</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddGuidelineDialogOpen(true)}
            >
              Add Guideline
            </Button>
          </Box>
          <OrganisationGuidelinesTable
            guidelines={guidelines}
            onRemove={handleRemoveGuideline}
            onRefresh={fetchGuidelines}
            organisationId={id || ''}
          />
        </Box>
      )}

      {activeTab === 2 && (
        <OrganisationUsersTab
          organisationId={id || ''}
          organisationName={organisation.name}
          onUsersChanged={fetchOrganisation}
        />
      )}

      {/* Edit Dialog */}
      <EditOrganisationDialog
        open={editDialogOpen}
        organisation={organisation}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          fetchOrganisation();
          setEditDialogOpen(false);
        }}
      />

      {/* Add Guideline Dialog */}
      <AddGuidelineDialog
        open={addGuidelineDialogOpen}
        organisationId={id || ''}
        existingGuidelineIds={guidelines.map(g => g.guideline_docs_id)}
        onClose={() => setAddGuidelineDialogOpen(false)}
        onSuccess={() => {
          fetchGuidelines();
          setAddGuidelineDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default OrganisationDetail;
